import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUser, fetchUser } from "../../../slices/userSlice";
import {toast} from "react-toastify";
import { uselocation } from "react-router-dom";


import './Login.scss';
import {
  Stack,
  TextField,
  Button, 
  InputAdornment,
  IconButton,
  Link,
  FormControl,
  FilledInput,
  InputLabel,
  CircularProgress,
  Typography,
  Alert,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

export default function Login({ timerKey = "resendBlockExpiry" }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const from = location.state?.from?.pathname;

  const globalError = useSelector((state) => state.user.error);
  const globalIsLoading = useSelector((state) => state.user.loading);
  const isVerified = useSelector((state) => state.user.isVerified);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isBlocked, setIsBlocked] = useState(() => {
    const expiry = localStorage.getItem(timerKey);
    return expiry ? Date.now() < parseInt(expiry, 10) : false;
  });
  const [timeLeft, setTimeLeft] = useState("");

  useEffect(() => {
    document.body.classList.add('login-page');
    const style = document.createElement('style');
    style.setAttribute('data-login-style', 'true');
    style.textContent = 'input[type="password"]::-ms-reveal,input[type="password"]::-ms-clear{display:none}';
    document.head.appendChild(style);

    return () => {
      document.body.classList.remove('login-page');
      style.remove();
    };
  }, []);
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const reason = params.get("reason");

        // Display appropriate toast messages based on the reason
        if (reason === "locked") {
          toast.error("Your account has been locked. Please contact support.");
        } else if (reason === "expired") {
          toast.error("Your session has expired. Please log in again.");
        }

      
        if (reason) {
          navigate("/login", { replace: true });
        }
      }, [location.search, navigate]);

  useEffect(() => {
    if (!isBlocked) return;

    const calculateTimeLeft = () => {
      const expiry = localStorage.getItem(timerKey);
      if (!expiry) return null;

      const difference = parseInt(expiry, 10) - Date.now();
      if (difference <= 0) {
        return null;
      }

      const minutes = Math.floor((difference / 1000 / 60) % 60);
      const seconds = Math.floor((difference / 1000) % 60);

      return `${minutes}m ${seconds}s`;
    }

    const initialTime = calculateTimeLeft();
    if (initialTime) {
      setTimeLeft(initialTime);
    } else {
      setIsBlocked(false);
      localStorage.removeItem(timerKey);
    }

    const timer = setInterval(() => {
      const remaining = calculateTimeLeft();
      if (remaining) {
        setTimeLeft(remaining);
      } else {
        setIsBlocked(false);
        localStorage.removeItem(timerKey);
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [isBlocked, timerKey]);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const validateField = (name, value) => {
    switch (name) {
      case 'email':
        if (!value) return "Email is required";
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? "" : "Invalid email format";
      case 'password':
        return value.length > 0 ? "" : "Password is required";
      default:
        return "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "email") setEmail(value);
    if (name === "password") setPassword(value);

    setErrors(prev => ({
      ...prev,
      [name]: validateField(name, value)
    }));
  };

  const isFormValid = 
    email.trim() !== '' && 
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && 
    password.trim() !== '' &&
    !errors.email && !errors.password;

  const handleSubmit = async (event) => {
    event.preventDefault();
    // Final validation check
    const emailErr = validateField('email', email);
    const passErr = validateField('password', password);

    if (emailErr || passErr) {
      setErrors({ email: emailErr, password: passErr });
      return;
    }

    const userdata = {
      "email": email.toLowerCase().trim(),
      "pass": password,
    };

    const resultAction = await dispatch(loginUser(userdata));
    if (loginUser.fulfilled.match(resultAction)) {
      await dispatch(fetchUser());
      navigate(from || "/", { replace: true});
    }
  };

  const passwordAdornment = (
    <InputAdornment position="end" data-testid="toggle-password-visibility">
      <IconButton
        aria-label={showPassword ? 'hide password' : 'display password'}
        onClick={handleClickShowPassword}
        edge="end"
        data-testid="password-visibility-toggle"
      >
        {showPassword ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <div className="login-page" data-testid="login-page">
      <div className="login-box-wrapper" data-testid="login-box-wrapper">
        <div className="form-container" data-testid="form-container">
          <Stack
            spacing={2}
            sx={{ maxWidth: 300, margin: 'auto', padding: 2 }}
            component="form"
            onSubmit={handleSubmit}
            data-testid="login-form"
          >
            <h1 className="WelcomeHeader" data-testid="welcome-header">Login</h1>

            {globalError && <Alert severity="error" data-testid="login-error-alert">{globalError}</Alert>}
            {/*Email Input Field/*/}
            <TextField
              id="email"
              data-testid="email-input"
              name="email"
              required
              label="Email"
              variant="filled"
              size="small"
              type="email"
              value={email}
              onChange={handleChange}
              fullWidth
              error={!!errors.email}
              helperText={errors.email}
              disabled={globalIsLoading}
            />
     
            {/*Password Input Field/*/}
            <FormControl variant="filled" fullWidth size="small" required error={!!errors.password} data-testid="password-form-control">
              <InputLabel htmlFor="password" data-testid="password-label">Password</InputLabel>
              <FilledInput
                id="password"
                data-testid="password-input"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={handleChange}
                endAdornment={passwordAdornment}
                disabled={globalIsLoading}
              />
              {errors.password && (
                <Typography color="error" variant="caption" sx={{ ml: 1.5, mt: 0.5 }} data-testid="password-error-text">
                  {errors.password}
                </Typography>
              )}
            </FormControl>
              
            <Button
              variant="contained"
              color="primary"
              size="small"
              type="submit"
              data-testid="loginButton"
              fullWidth
              disabled={globalIsLoading || !isFormValid}
            >
              {globalIsLoading ? <CircularProgress data-testid='loading-spin' size={24} color="inherit" /> : 'Login'}
            </Button>

            <h3 className="accountHeader" style={{ textAlign: 'center', marginTop: '0.5rem' }} data-testid="account-header">
              Don't have an account? <Link href="/Registration" data-testid="registrationForm">Register</Link>
            </h3>
            <h2 style={{ textAlign: 'center', fontFamily: '"Poppins", sans-serif'}} data-testid="forgot-password-header">
              <Link
            href="/email-verification"
            underline="hover"
            color="primary"
            className="forgot-password-link"
            data-testid="forgot-password-link"
          >
            Forgot password?
          </Link>
        </h2>
         {isVerified === false && !isBlocked && (
              <h2 style={{ textAlign: 'center', fontFamily: '"Poppins", sans-serif'}} data-testid="resend-email-link">
              <Link
            href="/resend-verification"
            underline="hover"
            color="primary"
            className="resend-email-link"
            data-testid="resend-email-link"
          >
            Resend Email Verification?
          </Link>
        </h2>
            )}
            {isVerified === false && isBlocked && (
              <h2 style={{ textAlign: 'center', fontFamily: '"Poppins", sans-serif'}} data-testid="resend-email-try-later">
              <Typography
            color="primary"
            className="resend-email-try-later"
            data-testid="resend-email-try-later"
          >
            Please wait {timeLeft} to try again.
          </Typography>
        </h2>
            )}
          </Stack>
        </div>
      </div>
    </div>
  );
}

 